import React, { useMemo, useEffect, useState } from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import SystemSettingsApi from '../../../api/SystemSettingsApi';
import { clearMultiCurrencySettingsCache } from '../../../utils/multiCurrencySettings';
import { getEntitlementsSection } from '../../../utils/entitlements';

const parseConfig = (config) => {
  if (!config) {
    return { datatype: 'text', picklist_values: [] };
  }
  if (typeof config === 'object') {
    return { datatype: 'text', picklist_values: [], ...config };
  }
  try {
    return { datatype: 'text', picklist_values: [], ...JSON.parse(config) };
  } catch {
    return { datatype: 'text', picklist_values: [] };
  }
};

const parseBoolean = (value) => {
  if (typeof value === 'boolean') return value;
  return String(value).toLowerCase() === 'true';
};

const SystemSettingsManager = () => {
  const api = useMemo(() => new SystemSettingsApi(), []);
  const [settings, setSettings] = useState([]);
  const [formValues, setFormValues] = useState({});
  const [configValues, setConfigValues] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');

  const showMultiCurrencySetting = useMemo(() => {
    const setup = getEntitlementsSection('setup', {});
    const allowed = Array.isArray(setup?.allowedItems) ? setup.allowedItems : [];
    return allowed.some((i) => String(i).toLowerCase() === 'currency');
  }, []);

  const visibleSettings = useMemo(() => {
    return settings.filter(
      (s) => s && (s.key !== 'multi_currency_enabled' || showMultiCurrencySetting)
    );
  }, [settings, showMultiCurrencySetting]);

  useEffect(() => {
    const load = async () => {
      const response = await api.fetchSettings();
      const result = response?.data;
      if (result?.success) {
        const payload = result.data;
        const list = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.settings)
            ? payload.settings
            : [];
        setSettings(list);
        const values = {};
        const configs = {};
        list.forEach((item) => {
          if (item && item.key) {
            const config = parseConfig(item.config);
            configs[item.key] = config;
            if (config.datatype === 'checkbox') {
              values[item.key] = parseBoolean(item.value);
            } else {
              values[item.key] = item.value ?? '';
            }
          }
        });
        setFormValues(values);
        setConfigValues(configs);
      }
    };
    load();
  }, [api]);

  const handleChange = (key, value) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
  };

  const renderValueInput = (setting) => {
    const config = configValues[setting.key] || { datatype: 'text' };
    const value = formValues[setting.key] ?? '';

    if (config.datatype === 'checkbox') {
      const isMultiCurrencyOnceEnabled =
        setting.key === 'multi_currency_enabled' && Boolean(value);
      return (
        <div>
          <Form.Check
            type="switch"
            id={`sys-${setting.key}`}
            label=""
            checked={Boolean(value)}
            onChange={(e) => handleChange(setting.key, e.target.checked)}
            disabled={isMultiCurrencyOnceEnabled}
          />
          {isMultiCurrencyOnceEnabled && (
            <Form.Text className="text-muted d-block mt-1">
              Once enabled, Multi Currency cannot be disabled.
            </Form.Text>
          )}
        </div>
      );
    }

    if (config.datatype === 'picklist') {
      const options = Array.isArray(config.picklist_values) ? config.picklist_values : [];
      const getOptionValue = (opt) => (typeof opt === 'object' && opt !== null && 'value' in opt ? opt.value : opt);
      const getOptionLabel = (opt) => (typeof opt === 'object' && opt !== null && 'label' in opt ? opt.label : opt);
      return (
        <Form.Select
          value={value}
          onChange={(e) => handleChange(setting.key, e.target.value)}
        >
          <option value="">Select</option>
          {options.map((opt, idx) => (
            <option key={getOptionValue(opt) || idx} value={getOptionValue(opt)}>
              {getOptionLabel(opt)}
            </option>
          ))}
        </Form.Select>
      );
    }

    const inputType = config.datatype === 'number' ? 'number' : 'text';
    return (
      <Form.Control
        type={inputType}
        value={value}
        onChange={(e) => handleChange(setting.key, e.target.value)}
      />
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    setMessage('');
    const updates = settings.map((setting) => {
      const config = configValues[setting.key] || { datatype: 'text' };
      let nextValue = formValues[setting.key];
      if (config.datatype === 'checkbox') {
        nextValue = String(Boolean(nextValue));
      }
      return {
        key: setting.key,
        label: setting.label || setting.key,
        value: nextValue === undefined ? '' : String(nextValue),
        config: setting.config || {},
      };
    });

    try {
      const response = await api.updateSettings(updates);
      const result = response?.data;
      if (result?.success) {
        setSettings(result.data || []);
        clearMultiCurrencySettingsCache();
        setMessage('Settings updated successfully.');
      } else {
        setMessage(result?.message || 'Failed to update settings.');
      }
    } catch (err) {
      setMessage(err?.response?.data?.message || err?.message || 'Failed to update.');
    }
    setIsSaving(false);
  };

  return (
    <div className="wrapper">
      <div id="content">
        <Card className="p-4">
          <h4 className="mb-4">System Settings</h4>
          {visibleSettings.map((setting) => (
            <Row className="align-items-center mb-3" key={setting.key}>
              <Col md={5}>
                <Form.Label className="mb-0">{setting.label || setting.key}</Form.Label>
              </Col>
              <Col md={4}>
                {renderValueInput(setting)}
              </Col>
            </Row>
          ))}
          {visibleSettings.length === 0 && (
            <p className="text-muted">No system settings defined.</p>
          )}
          {message && <div className="mt-2 text-muted">{message}</div>}
          <div className="mt-4">
            <Button variant="primary" onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default SystemSettingsManager;
